import {
  Autocomplete as MuiAutocomplete,
  autocompleteClasses,
  Box,
  Chip,
  chipClasses,
  createFilterOptions,
} from '@mui/material'
import { IconArrow, IconCross } from 'assets/icons'
import { MenuOption } from 'elements/navigation'
import { useBoolean } from 'lib/hooks'
import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '../checkbox'
import { FormControl, FormControlWrapper } from '../form'
import { InputBase } from '../input-base'
import { AutocompletePopper } from './autocomplete-popper'

type Option = {
  value: string
  label: string
  disabled?: boolean
  createNewOption?: boolean
}

export type MultipleAutocompleteProps = FormControlWrapper & {
  options: Array<Option>
  value: Array<string>
  onChange: (value: Array<string>) => void
  placeholder?: string
  disabled?: boolean

  enableCreatingNewOptions?: boolean
}

const defaultFilterOptions = createFilterOptions<Option>()

export const MultipleAutocomplete = forwardRef(
  (
    {
      onChange,
      value,
      options,
      label,
      error,
      id,
      helperText,
      onRemove,
      required,
      disabled,
      enableCreatingNewOptions,
      placeholder,
      ...rest
    }: MultipleAutocompleteProps,
    ref,
  ) => {
    const { t } = useTranslation()

    const isOpen = useBoolean()

    const [newOptions, setNewOptions] = useState<Array<Option>>([])

    const allOptions = [...options, ...newOptions]

    const handleDelete = (optionToDelete: Option) => () =>
      onChange(value.filter(option => option !== optionToDelete.value))

    const handleSelectAll = () => onChange(allOptions.map(({ value }) => value))

    const handleDeselectAll = () => onChange([])

    return (
      <FormControl
        {...rest}
        label={label}
        error={error}
        id={id}
        helperText={helperText}
        onRemove={onRemove}
        required={required}
      >
        <MuiAutocomplete
          multiple
          open={isOpen.isTrue}
          ref={ref}
          onOpen={isOpen.setTrue}
          onClose={isOpen.setFalse}
          options={[
            ...(allOptions.length > 1
              ? [
                  {
                    value: 'All',
                    label: t('common.all'),
                  },
                ]
              : []),
            ...allOptions.sort((a, b) => a.label.localeCompare(b.label)),
          ]}
          value={allOptions.filter(option => value.includes(option.value))}
          onChange={(_, options) => {
            onChange(options.map(option => option.value))
          }}
          disabled={disabled}
          getOptionLabel={option => option?.label}
          popupIcon={<IconArrow />}
          noOptionsText={t('common.nothing_found')}
          filterOptions={(options, state) => {
            const results = defaultFilterOptions(options, state)

            if (
              results.length === 0 &&
              enableCreatingNewOptions &&
              state.inputValue !== ''
            ) {
              return [
                {
                  value: state.inputValue,
                  label: `${t('projects.terms.create_tag')}: ${
                    state.inputValue
                  }`,
                  createNewOption: true,
                },
              ]
            }

            return results
          }}
          renderTags={(options, getTagProps) => {
            return options.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                sx={{
                  height: '24px',
                  px: 1.5,
                  mr: 1,
                  backgroundColor: 'rgb(97, 0, 220, 0.3)',

                  [`& .${chipClasses.label}`]: {
                    px: 0,
                    mr: 1,
                    color: 'text.primary',
                  },

                  [`& .${chipClasses.deleteIcon}`]: {
                    mx: 0,
                    color: 'text.primary',
                    fontSize: '8px',
                  },
                }}
                deleteIcon={<IconCross />}
                onDelete={handleDelete(option)}
              />
            ))
          }}
          renderInput={params => {
            return (
              <InputBase
                {...params.InputProps}
                inputProps={{
                  ...params.inputProps,
                  id: id ?? label,
                  type: 'search',
                  autoComplete: 'off',
                }}
                placeholder={placeholder ?? t('common.start_typing_to_find')}
                error={error}
                required={required}
              />
            )
          }}
          disableCloseOnSelect
          renderOption={(
            { className, onClick, ...optionProps },
            { disabled, label, createNewOption, value: currentOptionValue },
            { selected, inputValue },
          ) => {
            const isAllOption = currentOptionValue === 'All'
            return (
              <MenuOption
                {...optionProps}
                aria-selected={
                  isAllOption
                    ? value.length === allOptions.length
                    : optionProps['aria-selected']
                }
                disableRipple
                disabled={disabled}
                onClick={event => {
                  if (createNewOption) {
                    setNewOptions(prev => [
                      ...prev,
                      { value: inputValue, label: inputValue },
                    ])
                  }

                  if (isAllOption) {
                    if (value.length < allOptions.length) {
                      handleSelectAll()
                    } else {
                      handleDeselectAll()
                    }

                    return
                  }

                  onClick?.(event)
                }}
              >
                {!createNewOption && (
                  <Box mr={1}>
                    {isAllOption ? (
                      <Checkbox
                        checked={value.length === allOptions.length}
                        indeterminate={
                          value.length > 0 && value.length < allOptions.length
                        }
                      />
                    ) : (
                      <Checkbox checked={selected} />
                    )}
                  </Box>
                )}

                {label}
              </MenuOption>
            )
          }}
          PopperComponent={AutocompletePopper}
          sx={{
            width: '100%',

            [`& .${autocompleteClasses.inputRoot}`]: {
              width: '100%',
              height: 'auto',
              minHeight: 48,
              py: 0.5,
              px: 1,

              ...(isOpen.isTrue && {
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              }),

              [`& .${autocompleteClasses.input}`]: {
                py: 0.5,
                pl: 2,
                pr: 1,
                minWidth: 100,
              },
            },

            [`& .${autocompleteClasses.endAdornment}`]: {
              right: '8px',
            },
          }}
        />
      </FormControl>
    )
  },
)
